import type { Configuration } from '@azure/msal-browser';
import type { IdentityProvider } from '@senviro/core-api-client';

/**
 * MSAL.js configuration (W1-F6-S2).
 *
 * Reads from Vite's `import.meta.env`:
 *   VITE_ENTRA_CLIENT_ID  — multi-tenant app registration's client ID
 *   VITE_ENTRA_AUTHORITY  — `https://login.microsoftonline.com/common`
 *   VITE_ENTRA_REDIRECT_URI — `http://localhost:5173/auth/callback`
 *   VITE_ENTRA_SCOPES     — comma-separated, default `openid,profile,email`
 *
 * For zero-config local dev (`VITE_DEV_AUTH_BYPASS=true`), the AuthProvider
 * shortcuts the popup with a fixed dev-tenant account and the backend
 * accepts any token (or no token) — see W1-F6-S5.
 */

export const DEFAULT_ENTRA_AUTHORITY = 'https://login.microsoftonline.com/common';
export const DEFAULT_REDIRECT_URI = 'http://localhost:5173/auth/callback';
export const DEFAULT_SCOPES = ['openid', 'profile', 'email'];

export function readMsalEnv(): {
  clientId: string;
  authority: string;
  redirectUri: string;
  scopes: string[];
  devBypass: boolean;
} {
  const env = (import.meta as ImportMeta).env as Record<string, string | undefined>;
  const scopesRaw = env.VITE_ENTRA_SCOPES;
  const scopes = scopesRaw
    ? scopesRaw
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    : DEFAULT_SCOPES;
  // Treat any non-prod build as eligible for the bypass — Vite sets DEV=true
  // in dev, MODE='test' in vitest, and PROD=true only when running a prod
  // build. The bypass still requires the explicit VITE_DEV_AUTH_BYPASS opt-in.
  const isNonProd =
    env.PROD !== 'true' ||
    env.DEV === 'true' ||
    Boolean(env.DEV) ||
    env.MODE === 'test' ||
    env.MODE === 'development';
  return {
    clientId: env.VITE_ENTRA_CLIENT_ID ?? 'dev-client-id',
    authority: env.VITE_ENTRA_AUTHORITY ?? DEFAULT_ENTRA_AUTHORITY,
    redirectUri: env.VITE_ENTRA_REDIRECT_URI ?? DEFAULT_REDIRECT_URI,
    scopes,
    devBypass: isNonProd && env.VITE_DEV_AUTH_BYPASS === 'true',
  };
}

/**
 * `buildMsalConfig(provider?)` — produces an MSAL Configuration object.
 * When `provider` is non-null, the partner's `tenant_id` + `client_id`
 * override the platform-default authority + clientId so the user signs
 * in against the partner's Entra app registration (W5-F3-S2).
 *
 * Provider-type handling:
 *   - `entra_id`  — `https://login.microsoftonline.com/{tenant_id}/v2.0`
 *   - `azure_b2c` — uses `config.policy_authority` from the row's JSONB
 *   - `okta` / `auth0` — placeholder; throws to surface the unsupported
 *     case rather than silently picking the wrong authority. (Future
 *     work: add MSAL.js OIDC support OR swap to a separate adapter.)
 */
export function buildMsalConfig(
  provider?: IdentityProvider | null,
): Configuration {
  const env = readMsalEnv();
  const { authority: platformAuthority, redirectUri } = env;

  const auth =
    provider && provider.active
      ? buildAuthFromProvider(provider, redirectUri)
      : {
          clientId: env.clientId,
          authority: platformAuthority,
          redirectUri,
          postLogoutRedirectUri: redirectUri,
          navigateToLoginRequestUrl: true,
        };

  return {
    auth,
    cache: {
      cacheLocation: 'sessionStorage',
      storeAuthStateInCookie: false,
    },
  };
}

function buildAuthFromProvider(
  provider: IdentityProvider,
  redirectUri: string,
): Configuration['auth'] {
  if (provider.provider_type === 'entra_id') {
    return {
      clientId: provider.client_id,
      authority: `https://login.microsoftonline.com/${provider.tenant_id}/v2.0`,
      redirectUri,
      postLogoutRedirectUri: redirectUri,
      navigateToLoginRequestUrl: true,
    };
  }
  if (provider.provider_type === 'azure_b2c') {
    const config = (provider.config ?? {}) as { policy_authority?: string };
    if (!config.policy_authority) {
      throw new Error(
        `IdentityProvider ${provider.id} is azure_b2c but missing config.policy_authority`,
      );
    }
    return {
      clientId: provider.client_id,
      authority: config.policy_authority,
      redirectUri,
      postLogoutRedirectUri: redirectUri,
      navigateToLoginRequestUrl: true,
      knownAuthorities: [new URL(config.policy_authority).host],
    };
  }
  // okta / auth0 — fall through; unsupported by MSAL.js without an OIDC
  // adapter. Surface explicitly so the partner-onboarding flow can refuse
  // these provider types until W5-F3-S2-FOLLOWUP swaps in a generic OIDC
  // client.
  throw new Error(
    `IdentityProvider provider_type "${provider.provider_type}" is not yet supported by Senviro's MSAL adapter (W5-F3-S2-FOLLOWUP).`,
  );
}

/**
 * `useDevBypass()` — read-only hook returning `true` when the dev bypass is
 * active. Exists as a hook (not just a constant) so tests / Storybook can
 * mock it via the standard React module-mocking pattern.
 */
export function useDevBypass(): boolean {
  return readMsalEnv().devBypass;
}
